import axios from 'axios';
import { apiClient } from '@/shared/api/http-client';
import { createApiPath } from '@/shared/api/endpoints';

// OCR 관련 타입 정의
export interface OcrRequest {
  imageUrl: string;
}

export interface PromoLicense {
  registrationNumber?: string | null;
  /** OCR 응답 날짜. yyyy-MM-dd 또는 숫자만 있는 형태를 허용한다 */
  registrationStartDate?: string | null;
  registrationEndDate?: string | null;
}

export interface PrescriptionLine {
  drugCode: string;
  drugName: string;
  outpatientUsageAmount: number;
  inHouseUsageAmount: number;
}

export type BusinessLicenseTaxType =
  | 'CORPORATE'
  | 'GENERAL'
  | 'SIMPLIFIED'
  | 'TAX_FREE';

export interface BusinessLicense {
  address: string;
  businessName: string;
  registrationNumber: string;
  representativeName: string;
  taxEmail: string;
  /** OCR 응답은 yyMMdd 문자열. 수정 API 의 yyyy-MM-dd 와 다르다 */
  birthDate?: string | null;
  taxType?: BusinessLicenseTaxType | null;
  /**
   * 서버가 taxType 에서 계산한 값. 인식 실패면 null — 폼 유형을 바꾸지 않는다.
   */
  businessOwnerType?: 'INDIVIDUAL' | 'CORPORATE' | null;
}

export interface OcrCornerPayload {
  imageUrl: string;
  bbox?: { x: number; y: number; width: number; height: number };
  corners?: Array<{ x: number; y: number }>;
}

const OCR_CLIENT_BASE_URL =
  import.meta.env.VITE_OCR_CLIENT_BASE_URL ||
  'https://rx-ocr-dev-448580105039.asia-northeast3.run.app';

const ocrDirectClient = axios.create({
  timeout: 600_000,
  headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
});

// OCR 서비스
export const ocrService = {
  // 판촉 영업자 신고증 OCR
  ocrPromoLicense: async (request: OcrRequest): Promise<PromoLicense> => {
    const response = await apiClient.post(
      createApiPath.ocr.promoLicense(),
      request,
    );
    return response.data;
  },

  ocrPrescriptionImage: async (
    request: OcrRequest,
  ): Promise<PrescriptionLine[]> => {
    const response = await apiClient.post(
      createApiPath.ocr.prescription(),
      request,
    );
    return response.data;
  },

  ocrPrescriptionFile: async (file: File): Promise<PrescriptionLine[]> => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await apiClient.post(
      createApiPath.ocr.prescriptionFile(),
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } },
    );
    return response.data;
  },

  ocrBusinessLicense: async (request: OcrRequest): Promise<BusinessLicense> => {
    const response = await apiClient.post(
      createApiPath.ocr.businessLicense(),
      request,
    );
    return response.data;
  },

  ocrPrescriptionFromUrl: async (
    payload: OcrCornerPayload | { imageUrl: string },
  ) => {
    const response = await ocrDirectClient.post(
      `${OCR_CLIENT_BASE_URL}/document-ai/process-url`,
      payload,
    );
    return response.data;
  },

  ocrPrescriptionFromUrlGemini: async (
    payload: OcrCornerPayload | { imageUrl: string },
  ) => {
    const response = await ocrDirectClient.post(
      `${OCR_CLIENT_BASE_URL}/document-ai/process-url/gemini`,
      payload,
    );
    return response.data;
  },

  /** 하이라이터용 raw OCR — 레거시 requestEditedOCRraw */
  ocrPrescriptionFromUrlRaw: async (payload: { imageUrl: string }) => {
    const response = await ocrDirectClient.post(
      `${OCR_CLIENT_BASE_URL}/document-ai/process-url/raw`,
      payload,
    );
    return response.data;
  },

  searchDrugMatching: async (payload: {
    items: Array<{
      drugName: string;
      drugCode?: string;
      pharmaCompanyName?: string;
      unitPrice?: number | null;
      inQuantity?: number;
      outQuantity?: number;
    }>;
    limit?: number;
    yearMonth: string;
  }): Promise<{
    results: Array<{ candidates?: Array<Record<string, unknown>> }>;
    metadata?: { confidence?: string; reason?: string };
  }> => {
    const response = await ocrDirectClient.post(
      `${OCR_CLIENT_BASE_URL}/matching/search`,
      payload,
    );
    const data = response.data as {
      results?: Array<{ candidates?: Array<Record<string, unknown>> }>;
      metadata?: { confidence?: string; reason?: string };
    };
    return {
      results: data?.results ?? [],
      metadata: data?.metadata,
    };
  },
};
